/**
 * Route-specific report for one tracked contact, keyed by threat_key.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import { ThreatReport } from './threatReportView';
import { formatMeters } from './format';
import { metalSx } from '../../theme/metalButtons';

const GOLD = '#f5b942';
const CORAL = '#ef5350';
const MUTED = '#8a939f';
const EDGE = '#262b33';

const TONE: Record<ThreatReport['trust']['tone'], string> = {
  crit: CORAL,
  warn: GOLD,
  ok: '#66bb6a',
};

const KIND: Record<ThreatReport['kind'], string> = {
  watchlist: 'Watchlist',
  drone: 'Drone',
  cotraveler: 'Co-traveler',
};

export interface MissionThreatReportProps {
  report: ThreatReport;
  units?: 'km' | 'mi';
  onFocus?: (longitude: number, latitude: number) => void;
  onPromotePacket?: () => void;
  onRouteOptions?: () => void;
  onMarkOurs?: () => void;
  promoting?: boolean;
  routing?: boolean;
  marking?: boolean;
}

const Row: React.FC<{ label: string; children: React.ReactNode; tone?: string }> = ({
  label, children, tone,
}) => (
  <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', py: 0.2 }}>
    <Typography sx={{ width: 92, flexShrink: 0, fontSize: '0.6rem', color: MUTED }}>
      {label}
    </Typography>
    <Typography sx={{ flex: 1, fontSize: '0.68rem', color: tone || '#e8ebee', fontWeight: tone ? 700 : 500 }}>
      {children}
    </Typography>
  </Box>
);

const Head: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{
    mt: 1.1,
    mb: 0.35,
    fontSize: '0.6rem',
    fontWeight: 800,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: GOLD,
  }}
  >
    {children}
  </Typography>
);

const Btn: React.FC<{
  onClick?: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}> = ({ onClick, disabled, children }) => (
  <Box
    component="button"
    type="button"
    disabled={disabled || !onClick}
    onClick={(e) => { e.stopPropagation(); onClick?.(); }}
    sx={{
      ...metalSx('gun'),
      height: 26,
      px: 1.1,
      display: 'inline-flex',
      alignItems: 'center',
      fontSize: '0.6rem',
      fontWeight: 600,
      fontFamily: 'inherit',
      cursor: disabled || !onClick ? 'default' : 'pointer',
      opacity: disabled || !onClick ? 0.45 : 1,
    }}
  >
    {children}
  </Box>
);

const MissionThreatReport: React.FC<MissionThreatReportProps> = ({
  report,
  units = 'mi',
  onFocus,
  onPromotePacket,
  onRouteOptions,
  onMarkOurs,
  promoting,
  routing,
  marking,
}) => {
  const loc = report.location;
  const relBits = loc
    ? [
      loc.bearingDeg != null ? `${Math.round(loc.bearingDeg)}°` : null,
      loc.offsetM != null ? formatMeters(loc.offsetM, units) : null,
    ].filter(Boolean)
    : [];
  const rel = loc
    ? [
      relBits.join(' / ') || null,
      loc.leg != null ? `leg ${loc.leg}` : null,
    ].filter(Boolean).join(' · ')
    : null;

  return (
    <Box
      onClick={() => {
        if (loc) onFocus?.(loc.longitude, loc.latitude);
      }}
      sx={{ cursor: loc && onFocus ? 'pointer' : 'default' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 0.4 }}>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#f3f6f8' }}>
          {report.title}
        </Typography>
        <Typography sx={{ fontSize: '0.58rem', color: MUTED }}>
          {KIND[report.kind]} · {report.id}
        </Typography>
      </Box>
      <Row label="Trust state" tone={TONE[report.trust.tone]}>{report.trust.label}</Row>
      <Row label="First observed">{report.firstObservedDtg}</Row>

      <Head>Pattern of activity</Head>
      <Row label="Sightings">{report.sightings} across {report.places} place{report.places === 1 ? '' : 's'}</Row>
      {report.cotravel && <Row label="Co-travel">{report.cotravel}</Row>}
      {report.lastDwell && <Row label="Last dwell">{report.lastDwell}</Row>}

      {loc && (
        <>
          <Head>Location</Head>
          {loc.mgrs && <Row label="MGRS">{loc.mgrs}</Row>}
          <Row label="Decimal">{loc.decimal}</Row>
          {rel && <Row label="Rel. to route">{rel}</Row>}
          {loc.accuracyM != null && (
            <Row label="Accuracy">±{Math.round(loc.accuracyM)} m CEP</Row>
          )}
        </>
      )}

      <Head>Source and confidence</Head>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 800 }}>{report.source.grade}</Typography>
          <Typography sx={{ fontSize: '0.58rem', color: MUTED }}>{report.source.label}</Typography>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 800 }}>{report.confidence.label}</Typography>
          <Typography sx={{ fontSize: '0.58rem', color: MUTED }}>{report.confidence.detail}</Typography>
        </Box>
      </Box>
      <Typography sx={{ display: 'block', mt: 0.55, fontSize: '0.58rem', color: MUTED }}>
        {report.identifier}
        {report.location ? ` · ${report.location.decimal}` : ''}
      </Typography>

      <Box sx={{ display: 'flex', gap: 0.6, mt: 1, flexWrap: 'wrap', borderTop: `1px solid ${EDGE}`, pt: 0.85 }}>
        <Btn onClick={onPromotePacket} disabled={promoting}>
          {promoting ? 'Promoting…' : 'Promote to intel packet'}
        </Btn>
        <Btn onClick={onRouteOptions} disabled={routing || !report.canRouteAround}>
          {routing ? 'Routing…' : 'Route options'}
        </Btn>
        <Btn onClick={onMarkOurs} disabled={marking || !report.canMarkOurs}>
          {marking ? 'Marking…' : 'Mark false positive'}
        </Btn>
      </Box>
    </Box>
  );
};

export default MissionThreatReport;
